/**
 * Block time configurations for different blockchain networks
 */

/**
 * Average block times in seconds for different chain IDs
 */
export const BLOCK_TIMES: Record<number, number> = {
  1: 12,       // Ethereum Mainnet
  5: 12,       // Goerli (deprecated)
  11155111: 12, // Sepolia
  137: 2,      // Polygon
  80001: 2,    // Mumbai
  42161: 0.25, // Arbitrum One
  421614: 0.25, // Arbitrum Sepolia
  8453: 0.2,   // Base (with Flashblocks)
  84532: 0.2,  // Base Sepolia (with Flashblocks)
  56: 3,       // BSC
  97: 3,       // BSC Testnet
  10: 2,       // Optimism
  11155420: 2, // Optimism Sepolia
  43114: 2,    // Avalanche C-Chain
  43113: 2,    // Avalanche Fuji
  250: 1,      // Fantom
  4002: 1,     // Fantom Testnet
};

/**
 * Get block time in seconds for a given chain ID
 * @param chainId - The chain ID to get block time for
 * @returns Block time in seconds, defaults to 12 if chain is unknown
 */
export function getBlockTimeSeconds(chainId?: number): number {
  console.log('chainId', chainId);
  if (!chainId) return 12;
  return 2;
}

/**
 * Convert blocks to estimated time duration
 * @param blocks - Number of blocks
 * @param chainId - The chain ID to calculate for
 * @returns Object with duration in various units
 */
export function blocksToTime(blocks: number, chainId?: number) {
  const blockTime = getBlockTimeSeconds(chainId);
  const totalSeconds = blocks * blockTime;

  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return {
    totalSeconds,
    days,
    hours,
    minutes,
    seconds,
    formatted: formatDuration(days, hours, minutes)
  };
}

/**
 * Format duration in a human-readable string
 */
function formatDuration(days: number, hours: number, minutes: number): string {
  const parts = [];

  if (days > 0) {
    parts.push(`${days} day${days !== 1 ? 's' : ''}`);
  }
  if (hours > 0) {
    parts.push(`${hours} hour${hours !== 1 ? 's' : ''}`);
  }
  if (minutes > 0 && days === 0) { // Only show minutes if less than a day
    parts.push(`${minutes} minute${minutes !== 1 ? 's' : ''}`);
  }

  return parts.join(', ') || 'Less than a minute';
}

/**
 * Get a description of the block time for display
 * @param chainId - The chain ID
 * @returns Human-readable block time description
 */
export function getBlockTimeDescription(chainId?: number): string {
  const blockTime = getBlockTimeSeconds(chainId);

  if (blockTime < 1) {
    return `~${(blockTime * 1000).toFixed(0)}ms per block`;
  } else if (blockTime === 1) {
    return '~1 second per block';
  } else {
    return `~${blockTime} seconds per block`;
  }
}

/**
 * Calculate how many blocks are in a given time period
 * @param days - Number of days
 * @param chainId - The chain ID
 * @returns Number of blocks
 */
export function daysToBlocks(days: number, chainId?: number): number {
  const blockTime = getBlockTimeSeconds(chainId);
  const secondsPerDay = 86400;
  return Math.round((days * secondsPerDay) / blockTime);
}

/**
 * Get example calculations for UI display
 * @param chainId - The chain ID
 * @returns Array of example calculations
 */
export function getBlockTimeExamples(chainId?: number): string[] {
  const blockTime = getBlockTimeSeconds(chainId);

  // Calculate common durations
  const examples = [
    { blocks: 100, label: '100 blocks' },
    { blocks: 1000, label: '1,000 blocks' },
    { blocks: 10000, label: '10,000 blocks' },
  ];

  return examples.map(({ blocks, label }) => {
    const time = blocksToTime(blocks, chainId);
    return `${label} ≈ ${time.formatted}`;
  });
}
